import { useEffect } from "react";
import { usePurchaseContext } from "../../contexts/purchase";
import { useHeyFlowLeadContext } from "../../contexts/heyFlowLead";
import { KremierungsKategorie } from "../../data/pricing";
import { FancyFormTextInput } from "../FancyTextInput";
import { FormErrorMessage } from "../FormErrorMessage";
import { SelectBox } from "../SelectBox";

// Variant-Mapping: Widerristhöhen-Kategorie → WooCommerce-Variant-ID + Preis
const VARIANT_BY_CATEGORY: Record<
  KremierungsKategorie["id"],
  { variationId: number; price: number; name: string; animalType: "small" | "dog" | "cat" }
> = {
  pony_klein: {
    variationId: 74570,
    price: 1250,
    name: "Einzelkremierung Pony/Esel bis 1,30 m",
    animalType: "small",
  },
  pony_mittel: {
    variationId: 74572,
    price: 1550,
    name: "Einzelkremierung Pony/Esel 1,31–1,48 m",
    animalType: "dog",
  },
  pferd: {
    variationId: 74573,
    price: 1850,
    name: "Einzelkremierung Pferd ab 1,49 m",
    animalType: "cat",
  },
};

export function PurchasePetInfo() {
  const { purchaseInformation, errors, updateField } = usePurchaseContext();
  const { value, changeCartItem } = useHeyFlowLeadContext();

  // Helper: Cart-Variant auf die übergebene Kategorie umschalten.
  const syncCartVariant = (categoryId: KremierungsKategorie["id"]) => {
    if (!value) return;
    const variant = VARIANT_BY_CATEGORY[categoryId];
    const cremationIndex = value.cart.findIndex((i) => i.type === "cremation");
    if (cremationIndex === -1) return;
    const current = value.cart[cremationIndex];
    if (current.variationId !== variant.variationId) {
      changeCartItem(
        {
          ...current,
          variationId: variant.variationId,
          price: variant.price,
          name: variant.name,
        },
        cremationIndex
      );
    }
  };

  // Kategorie-Dropdown-Wechsel → Variant + Preis sofort anpassen
  useEffect(() => {
    const animalTypeToCategory: Record<string, KremierungsKategorie["id"]> = {
      small: "pony_klein",
      dog: "pony_mittel",
      cat: "pferd",
    };
    const categoryId = animalTypeToCategory[purchaseInformation?.animalType ?? ""];
    if (!categoryId) return;
    syncCartVariant(categoryId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [purchaseInformation?.animalType]);

  return (
    <>
      <div className="font-bold mt-7 mb-3">Infos zu deinem Pferd</div>
      <SelectBox
        options={[
          { text: "Welche Kategorie?", value: "none" },
          { text: "Pony/Esel bis 1,30 m", value: "small" },
          { text: "Pony/Esel 1,31–1,48 m", value: "dog" },
          { text: "Pferd ab 1,49 m", value: "cat" },
        ]}
        value={purchaseInformation?.animalType}
        setValue={(value) => updateField("animalType", value === "none" ? undefined : value)}
        defaultValue={"none"}
        hasError={errors.animalType !== undefined}
      />
      <div className="py-3">
        <FancyFormTextInput
          label="Name des Pferdes"
          type="text"
          formKey="animalName"
          formValue={purchaseInformation}
          setFormValue={updateField}
          errors={errors}
        />
      </div>
      <div className="pb-3">
        <FancyFormTextInput
          label="Gewicht des Pferdes (kg)"
          type="text"
          formKey="animalWeight"
          formValue={purchaseInformation}
          setFormValue={updateField}
          errors={errors}
        />
      </div>
      <SelectBox
        options={[
          { text: "Bereits verstorben", value: "dead" },
          { text: "Tierarzt-Termin steht bevor", value: "soon" },
          { text: "Vorsorglicher Auftrag", value: "forehand" },
        ]}
        value={purchaseInformation?.animalStage}
        setValue={(value) => updateField("animalStage", value)}
        hasError={errors.animalStage !== undefined}
      />
      <FormErrorMessage formKeys={["animalType", "animalName", "animalWeight", "animalStage"]} />
    </>
  );
}
